import { BrowserRouter, Route, Routes } from "react-router-dom";
import Home from "./views/Home";
import { Game } from "./views/Game";
import NewsArticle from "./views/NewsArticle.tsx";
import Nsa from "./articles/Nsa";
import Mit from "./articles/Mit";
import Fingerpointing from "./articles/Fingerpointing";
import World from "./articles/World";
import Ap from "./articles/Ap";
import Flash1 from "./articles/1";
import Flash2 from "./articles/2";
import Flash3 from "./articles/3";
import Flash4 from "./articles/4";
import Flash5 from "./articles/5";
import Flash6 from "./articles/6";
import Long1 from "./articles/l1";
import Long2 from "./articles/l2";
import Long3 from "./articles/l3";
import Long4 from "./articles/l4";
import Context from "./views/Context.tsx";
import Odds from "./views/Odds.tsx";
import Introduction from "./views/Introduction.tsx";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import Account from "./views/Account.tsx";
import {
  AccountStruct,
  playerJoin,
  loginWithHash,
  ErrorMessage,
  LoginResponse,
  setPlayerProfile,
  UGLY_USER_STATE,
  // getPlayerScoreboard,
} from "./api.ts";
import {
  createWebSocket,
  startPingPong,
  WebSocketHandlers,
} from "./websocketService.ts";
import Issues from "./views/Issues.tsx";
import Leaderboard from "./views/Leaderboard.tsx";
import { VideoBackground } from "./components/VideoBackground.tsx";
import Rubio from "./articles/Rubio.tsx";
import { EVENTS_NAME } from "./game/consts.ts";
import { EventBus } from "./game/EventBus.ts";
import { Toasts, toast } from "./components/Toasts.tsx";
import { ProjectorQR } from "./components/ProjectorQR.tsx";

export interface MusicState {
  muted: boolean;
  volume: number;
}

export const MusicContext = createContext<
  [MusicState, (_: MusicState) => void]
>([{ muted: true, volume: 0.25 }, (_) => {}]);

export const AccountContext = createContext<
  [AccountStruct | null, (_: AccountStruct | null) => void]
>([null, (_) => {}]);

export const WebSocketContext = createContext<WebSocket | null>(null);

export interface ChallengeContextInterface {
  interactive: boolean;
  challengeId?: number;
  objectiveId?: number;
  missionGiverId?: number;
  challengeTitle?: string;
  challengeText?: string;
  challengeGiver?: string;
  challengeDialogOpen?: boolean;
  // Separate from challengeDialogOpen: true once InteractNPCResponse has
  // delivered this challenge's data (drives which "?" button icon shows —
  // enabled vs. disabled), independent of whether the panel is actually
  // expanded. Walking into a zone makes a challenge AVAILABLE; it does not
  // open anything. Only the explicit button click sets challengeDialogOpen.
  challengeAvailable?: boolean;
  // Rich-challenge rendering: renderer key + server-authored JSON content.
  challengeType?: string;
  challengePayload?: string;
  // Server-computed (ClientMission.all_objectives_complete): true once every
  // objective on this mission is done for this player. Multi-objective
  // missions serve one objective at a time (see websocketService.ts), so
  // this is the only reliable "fully won" signal on the client.
  allObjectivesComplete?: boolean;
  // How many objectives this mission has in total (ClientMission.total_objectives).
  // >1 means "exit and re-enter the zone to see the next one" applies.
  totalObjectives?: number;
}

export const ChallengeContext = createContext<
  [ChallengeContextInterface, (_: ChallengeContextInterface) => void]
>([{ interactive: false, challengeDialogOpen: false }, (_) => {}]);

function App() {
  // Music defaults to MUTED — the player opts in via the speaker icon.
  const [music, setMusic] = useState<MusicState>({
    muted: true,
    volume: 0.25,
  });
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const [account, setAccount] = useState<AccountStruct | null>(
    localStorage.getItem("account")
      ? JSON.parse(localStorage.getItem("account")!)
      : null,
  );
  const [joinedGame, setJoinedGame] = useState(false);
  const [bootedGame, setBootedGame] = useState(false);
  // Whether the CURRENT websocket is open. Drives the early join (right after
  // login, on the account screen) instead of waiting for the Phaser game.
  const [wsOpen, setWsOpen] = useState(false);
  // Ensures the avatar/profile is pushed to the server exactly once per game
  // entry (the join no longer sends it — see the join effect).
  const profileSentForWsRef = useRef<WebSocket | null>(null);

  // Guards against StrictMode double-effects / re-renders racing the connect +
  // join flow. Each REST login mints a NEW magic and the dispatcher keeps only
  // the latest per account (and consumes it on any join attempt), so duplicate
  // login+join attempts invalidate each other ("magic does not match" → no one
  // gets registered with the game server).
  const wsInitStartedRef = useRef(false);
  const joinStartedForWsRef = useRef<WebSocket | null>(null);

  const [challengeContext, setChallengeContext] =
    useState<ChallengeContextInterface>({
      interactive: false,
      challengeDialogOpen: false,
    });

  const localStorageWrappedSetAccount = useCallback(
    (account: AccountStruct | null) => {
      if (account == null) {
        // console.log("localStorageWrappedSetAccount: clearing account");
        setAccount(null);
        localStorage.removeItem("account");
        // Also forget who the "local player" was — a stale name here makes
        // the next account's own PlayerInfo look like a remote player's.
        UGLY_USER_STATE.length = 0;
      } else {
        // console.log("localStorageWrappedSetAccount: set account");
        setAccount((prevState) => {
          // Merge only same-account updates. Merging across DIFFERENT
          // accounts builds a Frankenstein (new name + old position/score)
          // that then gets reported to the server and clobbers the new
          // account's save.
          const sameAccount =
            !prevState?.account_name ||
            !account.account_name ||
            prevState.account_name === account.account_name;
          const acct = sameAccount ? { ...prevState, ...account } : account;
          localStorage.setItem("account", JSON.stringify(acct));
          return acct;
        });
      }
    },
    [],
  );

  const initializeWebSocket = useCallback(() => {
    if (websocket === null) {
      // Create handlers for the websocket service
      const handlers: WebSocketHandlers = {
        setAccount: localStorageWrappedSetAccount,
        setChallengeContext: setChallengeContext,
        // The dispatcher rejected our PlayerJoin (stale/consumed magic).
        // Wipe the cached account+magic and reset the join guards so the
        // user is sent back to /account (Game view's account guard) and a
        // fresh login can join cleanly on this same socket.
        onJoinRejected: () => {
          joinStartedForWsRef.current = null;
          setJoinedGame(false);
          localStorageWrappedSetAccount(null);
        },
      };
      // Create the websocket using the service
      return createWebSocket(handlers, (newWs) => {
        setWebsocket(newWs);
      });
    }
  }, [localStorageWrappedSetAccount, websocket]);

  // The bootedGame listener's lifecycle is tied to the COMPONENT, not the
  // websocket. It used to be registered once at first socket creation and
  // REMOVED on every reconnect — so any reconnect that happened before the
  // Phaser game booted permanently lost the boot signal: bootedGame stayed
  // false, the join effect never fired, no PlayerJoin was ever sent, and the
  // player walked an un-joined world (PlayerNotFound on every interaction)
  // until a full page reload. Register once; clean up on unmount only.
  useEffect(() => {
    const onBootedGame = () => {
      console.log("bootedGame event received");
      setBootedGame(true);
    };
    EventBus.on(EVENTS_NAME.bootedGame, onBootedGame);
    return () => {
      EventBus.removeListener(EVENTS_NAME.bootedGame, onBootedGame);
    };
  }, []);

  // Track whether the websocket is open (component-scoped, like bootedGame):
  // websocketService emits wsOpen from ws.onopen. This gates the early join.
  useEffect(() => {
    const onWsOpen = () => {
      console.log("wsOpen event received");
      setWsOpen(true);
    };
    EventBus.on(EVENTS_NAME.wsOpen, onWsOpen);
    return () => {
      EventBus.removeListener(EVENTS_NAME.wsOpen, onWsOpen);
    };
  }, []);

  // A new socket (initial or reconnect) starts closed; reflect its live state
  // so a reconnect re-arms the join path. If it's somehow already open, catch
  // that too (the wsOpen event may have fired before this ran).
  useEffect(() => {
    setWsOpen(websocket?.readyState === WebSocket.OPEN);
  }, [websocket]);

  // useEffect(() => {
  //   const playerScoreboardTimer = setInterval(() => {
  //     if (websocket) {
  //       getPlayerScoreboard(websocket);
  //     }
  //   }, 1000 * 10);
  //   return () => {
  //     clearInterval(playerScoreboardTimer);
  //   };
  // }, [websocket]);

  useEffect(() => {
    console.log(`joined game: ${joinedGame}`);
  }, [joinedGame]);

  // Join the game EARLY — as soon as we have an account and an open socket,
  // not waiting for the Phaser game to boot. The PlayerJoin -> PlayerInfo
  // round-trip populates the account with the player's saved state (incl.
  // player_sprite_id), so the account screen can show their previous avatar.
  // NOTE: this deliberately does NOT setPlayerProfile — that would overwrite
  // the server's saved avatar with a default before PlayerInfo arrives. The
  // avatar is pushed at game entry (the effect below).
  useEffect(() => {
    setTimeout(async () => {
      if (account && websocket && !joinedGame && wsOpen) {
        // if we have a magic, we can immediately join the game; to do this, we use the login command to log in
        // get a magic, and playerjoin like usual
        if (account.eternal_magic_hash) {
          // Single-flight per socket: a second loginWithHash would mint a new
          // magic and invalidate the join below.
          if (joinStartedForWsRef.current === websocket) {
            return;
          }
          joinStartedForWsRef.current = websocket;
          console.log("Login to game with eternal hash");
          let response: LoginResponse | ErrorMessage;
          try {
            // login to get magic
            response = await loginWithHash(
              account.account_name,
              account.eternal_magic_hash,
            );
          } catch (e) {
            // NETWORK failure, not a rejection — loginWithHash rethrows on a
            // fetch/parse error but RETURNS an ErrResponse when the server
            // actually says no. The stored hash may be perfectly good here,
            // so do NOT discard the session; just unlatch so the next render
            // can retry. (Previously this threw out of the async callback
            // uncaught, leaving the guard latched: no join, no toast, no
            // retry — wedged until a reload.)
            console.error("eternal-hash login failed to reach the server", e);
            joinStartedForWsRef.current = null;
            toast(
              "Connection Problem",
              "Couldn't reach the login server. Retrying...",
              "error",
            );
            return;
          }
          if ((response as ErrorMessage).ErrResponse) {
            // The SERVER rejected the stored hash. Per ro-login's
            // is_correct_eternal that means no cookie on file, expired, or
            // mismatched — none of which can start working again. And the
            // hash lives in localStorage, so a reload just replays the exact
            // same failure: that is why reloading never helped.
            //
            // Discard every trace of the session and let the account guard
            // drop them on a clean login page.
            console.warn(
              "eternal hash rejected by the server; clearing session",
              response,
            );
            joinStartedForWsRef.current = null;
            profileSentForWsRef.current = null;
            setJoinedGame(false);
            // Clears React state, localStorage["account"] (where the dead
            // hash lives) and UGLY_USER_STATE (the local-player identity).
            localStorageWrappedSetAccount(null);
            toast(
              "Session Expired",
              "Your saved session is no longer valid. Please log in again.",
              "error",
            );
          } else {
            // TODO do this in playerjoin response?
            setJoinedGame(true);
            // use magic
            setTimeout(() => {
              playerJoin(
                websocket,
                account.account_name,
                (response as LoginResponse).LoginActionResponse.magic!,
              );
            }, 1000);
          }
        } else {
          // this is OK
          console.log(
            "No eternal magic hash, waiting for/in process of player join",
          );
        }
      }
    }, 1000);
    // localStorageWrappedSetAccount is a useCallback([]) — referentially
    // stable, so listing it satisfies the lint without causing re-runs.
  }, [account, wsOpen, joinedGame, websocket, localStorageWrappedSetAccount]);

  // Push the chosen avatar to the server once, when the player actually enters
  // the game (Phaser booted) and is joined. By now account.player_sprite_id is
  // the real value — either the server's saved sprite (from the early join's
  // PlayerInfo) or the user's fresh pick — so we never clobber it with a default.
  useEffect(() => {
    if (
      account &&
      websocket &&
      joinedGame &&
      bootedGame &&
      profileSentForWsRef.current !== websocket
    ) {
      profileSentForWsRef.current = websocket;
      setPlayerProfile(websocket, account.player_sprite_id || 1);
    }
  }, [account, websocket, joinedGame, bootedGame]);

  useEffect(() => {
    // Ref guard (not just state) so StrictMode's double-run of this effect
    // can't open two websockets — the second socket's join would invalidate
    // the first one's magic. Reconnects go through the service's onclose path,
    // not this effect.
    if (websocket === null && !wsInitStartedRef.current) {
      wsInitStartedRef.current = true;
      console.log("====>SETTING WEBSOCKET");
      const ws = initializeWebSocket();
      if (ws) {
        setWebsocket(ws);
      }
    }
  }, [initializeWebSocket, websocket]);

  useEffect(() => {
    if (websocket) {
      // console.log("Start playing echo pong");
    }
    // Use the startPingPong function from websocketService
    const pongInterval = websocket ? startPingPong(websocket) : null;

    return () => {
      if (pongInterval) {
        // console.log("Clearing pong game");
        clearInterval(pongInterval);
      }
    };
  }, [websocket]);

  return (
    <MusicContext.Provider value={[music, setMusic]}>
      <AccountContext.Provider value={[account, localStorageWrappedSetAccount]}>
        <WebSocketContext.Provider value={websocket}>
          <ChallengeContext.Provider
            value={[challengeContext, setChallengeContext]}
          >
            <BrowserRouter>
              <VideoBackground />
              <Toasts />
              <ProjectorQR />
              <Routes>
                <Route path="/" element={<Home />} />

                <Route path="/context" element={<Context />} />
                <Route path="/nsa" element={<NewsArticle Article={Nsa} />} />
                <Route path="/ap" element={<NewsArticle Article={Ap} />} />
                <Route
                  path="/world"
                  element={<NewsArticle Article={World} />}
                />
                <Route path="/mit" element={<NewsArticle Article={Mit} />} />
                <Route
                  path="/fingerpointing"
                  element={<NewsArticle Article={Fingerpointing} />}
                />
                <Route
                  path="/flash1"
                  element={<NewsArticle Article={Flash1} />}
                />
                <Route
                  path="/flash2"
                  element={<NewsArticle Article={Flash2} />}
                />
                <Route
                  path="/flash3"
                  element={<NewsArticle Article={Flash3} />}
                />
                <Route
                  path="/flash4"
                  element={<NewsArticle Article={Flash4} />}
                />
                <Route
                  path="/flash5"
                  element={<NewsArticle Article={Flash5} />}
                />
                <Route
                  path="/flash6"
                  element={<NewsArticle Article={Flash6} />}
                />
                <Route
                  path="/long1"
                  element={<NewsArticle Article={Long1} />}
                />
                <Route
                  path="/long2"
                  element={<NewsArticle Article={Long2} />}
                />
                <Route
                  path="/long3"
                  element={<NewsArticle Article={Long3} />}
                />
                <Route
                  path="/long4"
                  element={<NewsArticle Article={Long4} />}
                />
                <Route
                  path="/rubio"
                  element={<NewsArticle Article={Rubio} />}
                />
                <Route path="/introduction" element={<Introduction />} />
                <Route path="/odds" element={<Odds />} />

                <Route path="/account" element={<Account />} />
                <Route path="/game" element={<Game />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/issues" element={<Issues />} />
              </Routes>
            </BrowserRouter>
          </ChallengeContext.Provider>
        </WebSocketContext.Provider>
      </AccountContext.Provider>
    </MusicContext.Provider>
  );
}

export default App;
